import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMemo, useState, type ReactNode } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import type { InsuranceEntry, VerificationEntry, VerificationStatus } from './types';
import { formatDate, formatMoney, priorityLabel, prioritySortKey, verificationStatusBadge } from './helpers';
import { DUMMY_INSURANCES, DUMMY_VERIFICATIONS } from './mockData';

export interface VerificationsSectionProps {
  insurances: InsuranceEntry[];
  verifications: VerificationEntry[];
  onVerificationsChange: (verifications: VerificationEntry[]) => void;
  /** When set, Add Verification opens the canonical manual VOB form instead of local dummy rows. */
  onAddManualVerification?: () => void;
  /** Optional history table rendered below current verifications in the same card. */
  historySlot?: ReactNode;
}

export function VerificationsSection({
  insurances,
  verifications,
  onVerificationsChange,
  onAddManualVerification,
  historySlot,
}: VerificationsSectionProps): JSX.Element {
  const [addVerificationOpen, setAddVerificationOpen] = useState(false);
  const [draftVerification, setDraftVerification] = useState<{
    insuranceId: string;
    status: VerificationStatus;
    copayCents: string;
    coinsurancePercent: string;
    deductibleCents: string;
    deductibleRemainingCents: string;
  } | null>(null);

  const sortedInsurances = useMemo(
    () => [...insurances].sort((a, b) => prioritySortKey(a.priority) - prioritySortKey(b.priority)),
    [insurances],
  );

  type VerificationRow = VerificationEntry & { plan?: InsuranceEntry };

  const verificationsWithPlan = useMemo((): VerificationRow[] => {
    return verifications.map((v) => {
      const plan = insurances.find((i) => i.id === v.insuranceId);
      return { ...v, plan };
    });
  }, [verifications, insurances]);

  const verificationColumns = useMemo((): UnifyTableColumn<VerificationRow>[] => {
    return [
      {
        key: 'provider',
        label: 'Provider',
        render: (v) => <Text size="sm" fw={600}>{v.provider}</Text>,
        getValue: (v) => v.provider,
      },
      {
        key: 'serviceType',
        label: 'Service type',
        render: (v) => <Text size="sm">{v.billingCode ?? '—'}</Text>,
        getValue: (v) => v.billingCode ?? '',
      },
      {
        key: 'status',
        label: 'Status',
        render: (v) => {
          const statusBadge = verificationStatusBadge(v.status);
          return (
            <Badge variant="light" color={statusBadge.color} size="sm">
              {statusBadge.label}
            </Badge>
          );
        },
        getValue: (v) => verificationStatusBadge(v.status).label,
      },
      {
        key: 'copay',
        label: 'Co-pay',
        render: (v) => <Text size="sm">{v.copayCents != null ? formatMoney(v.copayCents) : '—'}</Text>,
        getValue: (v) => (v.copayCents != null ? formatMoney(v.copayCents) : ''),
      },
      {
        key: 'deductible',
        label: 'Deductible',
        render: (v) => (
          <Text size="sm">
            {v.deductibleCents != null ? formatMoney(v.deductibleCents) : '—'}
            {v.deductibleRemainingCents != null ? ` (${formatMoney(v.deductibleRemainingCents)} remaining)` : ''}
          </Text>
        ),
        getValue: (v) =>
          v.deductibleCents != null ? formatMoney(v.deductibleCents) : '',
      },
      {
        key: 'verified',
        label: 'Verified',
        render: (v) => (
          <Text size="sm" c="gray.6">
            {v.verifiedAt ? formatDate(v.verifiedAt) : '—'}
          </Text>
        ),
        getValue: (v) => (v.verifiedAt ? formatDate(v.verifiedAt) : ''),
      },
      {
        key: 'plan',
        label: 'Priority',
        render: (v) => (
          <Badge variant="light" color="blue" size="sm">
            {priorityLabel(v.priority)}
          </Badge>
        ),
        getValue: (v) => priorityLabel(v.priority),
      },
    ];
  }, []);

  const openAddVerification = () => {
    if (onAddManualVerification) {
      onAddManualVerification();
      return;
    }
    const first = sortedInsurances[0];
    if (!first) {
      notifications.show({ color: 'yellow', title: 'No plans', message: 'Add an insurance plan first.' });
      return;
    }
    setDraftVerification({
      insuranceId: first.id,
      status: 'current',
      copayCents: '',
      coinsurancePercent: '',
      deductibleCents: '',
      deductibleRemainingCents: '',
    });
    setAddVerificationOpen(true);
  };

  const saveVerification = () => {
    if (!draftVerification) return;
    const plan = insurances.find((i) => i.id === draftVerification.insuranceId);
    if (!plan) {
      notifications.show({ color: 'red', title: 'Invalid plan', message: 'Select an insurance plan.' });
      return;
    }
    const copayCents = draftVerification.copayCents ? Math.round(parseFloat(draftVerification.copayCents) * 100) : undefined;
    const deductibleCents = draftVerification.deductibleCents ? Math.round(parseFloat(draftVerification.deductibleCents) * 100) : undefined;
    const deductibleRemainingCents = draftVerification.deductibleRemainingCents ? Math.round(parseFloat(draftVerification.deductibleRemainingCents) * 100) : undefined;
    const coinsurancePercent = draftVerification.coinsurancePercent ? Number(draftVerification.coinsurancePercent) : undefined;
    const updated = [
      ...verifications,
      {
        id: `ver-${Math.random().toString(16).slice(2)}`,
        insuranceId: draftVerification.insuranceId,
        provider: plan.provider ?? '—',
        priority: plan.priority,
        status: draftVerification.status,
        copayCents,
        coinsurancePercent,
        deductibleCents,
        deductibleRemainingCents,
        verifiedAt: new Date().toISOString(),
      },
    ];
    onVerificationsChange(updated);
    setAddVerificationOpen(false);
    setDraftVerification(null);
    notifications.show({ color: 'green', title: 'Saved', message: 'Insurance verification added.' });
  };

  return (
    <>
      <Card withBorder>
        <Group justify="space-between" align="flex-start" wrap="nowrap" mb="md">
          <div style={{ flex: 1, minWidth: 0 }}>
            <Title order={4}>Verifications</Title>
            <Text size="sm" c="gray.6" mt="xs">
              Co-pay, co-insurance, and deductible by plan (aligned with CoverageEligibilityResponse)
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            disabled={onAddManualVerification ? false : sortedInsurances.length === 0}
            onClick={openAddVerification}
            style={{ flexShrink: 0 }}
          >
            Add Verification
          </Button>
        </Group>
        {verificationsWithPlan.length === 0 ? (
          <Text size="sm" c="gray.6">No verifications on file. Add a plan above, then add a verification.</Text>
        ) : (
          <UnifyTable
            withPaper={false}
            columns={verificationColumns}
            data={verificationsWithPlan}
            getRowKey={(v) => v.id}
          />
        )}
        {historySlot ? (
          <>
            <Divider my="lg" />
            {historySlot}
          </>
        ) : null}
      </Card>

      <Modal
        opened={addVerificationOpen}
        onClose={() => { setAddVerificationOpen(false); setDraftVerification(null); }}
        title="Add Verification"
        size="md"
      >
        {draftVerification ? (
          <Stack gap="md">
            <Text size="xs" c="gray.6">
              Benefit values map to Medplum CoverageEligibilityResponse (copay, coinsurance, deductible).
            </Text>
            <Select
              label="Insurance plan"
              value={draftVerification.insuranceId}
              onChange={(v) => v && setDraftVerification((prev) => prev ? { ...prev, insuranceId: v } : prev)}
              data={sortedInsurances.map((ins) => ({
                value: ins.id,
                label: `${priorityLabel(ins.priority)} — ${ins.provider ?? '—'}`,
              }))}
            />
            <Select
              label="Verification status"
              value={draftVerification.status}
              onChange={(v) => v && (v === 'current' || v === 'pending' || v === 'deprecated') && setDraftVerification((prev) => prev ? { ...prev, status: v } : prev)}
              data={[
                { value: 'current', label: 'Current' },
                { value: 'pending', label: 'Pending' },
                { value: 'deprecated', label: 'Deprecated' },
              ]}
            />
            <NumberInput
              label="Co-pay ($)"
              min={0}
              decimalScale={2}
              value={draftVerification.copayCents ? Number(draftVerification.copayCents) : undefined}
              onChange={(v) => setDraftVerification((prev) => prev ? { ...prev, copayCents: String(v ?? '') } : prev)}
              placeholder="e.g. 45"
            />
            <NumberInput
              label="Co-insurance (%)"
              min={0}
              max={100}
              value={draftVerification.coinsurancePercent ? Number(draftVerification.coinsurancePercent) : undefined}
              onChange={(v) => setDraftVerification((prev) => prev ? { ...prev, coinsurancePercent: String(v ?? '') } : prev)}
              placeholder="e.g. 10"
            />
            <NumberInput
              label="Deductible ($)"
              min={0}
              decimalScale={2}
              value={draftVerification.deductibleCents ? Number(draftVerification.deductibleCents) : undefined}
              onChange={(v) => setDraftVerification((prev) => prev ? { ...prev, deductibleCents: String(v ?? '') } : prev)}
              placeholder="e.g. 500"
            />
            <NumberInput
              label="Deductible remaining ($)"
              min={0}
              decimalScale={2}
              value={draftVerification.deductibleRemainingCents ? Number(draftVerification.deductibleRemainingCents) : undefined}
              onChange={(v) => setDraftVerification((prev) => prev ? { ...prev, deductibleRemainingCents: String(v ?? '') } : prev)}
              placeholder="e.g. 250"
            />
            <Divider />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => { setAddVerificationOpen(false); setDraftVerification(null); }}>Cancel</Button>
              <Button onClick={saveVerification}>Add verification</Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </>
  );
}

/** Standalone version with built-in state for use when parent doesn't manage state */
export function VerificationsSectionStandalone(): JSX.Element {
  const [insurances] = useState<InsuranceEntry[]>(DUMMY_INSURANCES);
  const [verifications, setVerifications] = useState<VerificationEntry[]>(DUMMY_VERIFICATIONS);
  return <VerificationsSection insurances={insurances} verifications={verifications} onVerificationsChange={setVerifications} />;
}
